import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { getBesoinAppro, creerCommandesDepuisBesoins,
         getArticlePrevisionVentes, getArticlePrevisionOR, getArticlePrevisionAchats,
         updateStockSeuils, patchArticleFournisseurPrefere, getEntreprises,
         getCandidatsAggregation } from '../api';
import { toast } from 'sonner';
import { RefreshCw, ShoppingBag, AlertTriangle, ArrowRight, Package, X, Pencil, Building2, Search } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useSociete } from '../context/SocieteContext';

// ── Badges source ─────────────────────────────────────────────────────────────
function SourceBadge({ source }) {
  if (source === 'seuil_min') return (
    <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-medium"
      style={{ background: '#FEF3C7', color: '#B45309' }}>
      <AlertTriangle size={10} /> Seuil min
    </span>
  );
  if (source === 'ventes_sv') return (
    <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-medium"
      style={{ background: '#EFF6FF', color: '#1D4ED8' }}>
      <ShoppingBag size={10} /> Ventes
    </span>
  );
  return (
    <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-medium"
      style={{ background: '#FEE2E2', color: '#B91C1C' }}>
      <Package size={10} /> OR bloqué
    </span>
  );
}

// ── Cellule cliquable (Rés. Ventes / Rés. OR / Cde Fourn.) ───────────────────
const POPOVER_COLORS = { ventes: '#B45309', or: '#7C3AED', achats: '#0369A1' };

function ClickableQty({ value, color, onClick, testId }) {
  if (!value || value === 0) return (
    <span className="text-sm tabular-nums" style={{ color: 'var(--text-muted)' }}>—</span>
  );
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className="text-sm font-semibold tabular-nums px-2 py-0.5 rounded-lg transition-all hover:opacity-80 active:scale-95"
      style={{ background: color + '18', color, border: `1px solid ${color}30` }}
    >
      {value}
    </button>
  );
}

// ── Popover détail ────────────────────────────────────────────────────────────
const POPOVER_LABELS = { ventes: 'Commandes ventes', or: 'Ordres de réparation', achats: 'Commandes fournisseurs' };

function PrevisionsPopover({ popover, onClose, onNavigate }) {
  if (!popover.open) return null;
  const { rect, type, agence_nom, items, loading } = popover;
  const color = POPOVER_COLORS[type];
  const top = Math.min(rect.bottom + 8, window.innerHeight - 320);
  const left = Math.max(8, Math.min(rect.left, window.innerWidth - 340));

  return createPortal(
    <>
      <div className="fixed inset-0 z-[998]" onClick={e => { e.stopPropagation(); onClose(); }} />
      <div className="fixed z-[999] rounded-xl shadow-2xl border overflow-hidden"
        style={{ top, left, width: 320, background: '#ffffff', borderColor: '#E2E8F0' }}
        data-testid="reassort-previsions-popover"
        onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-3 py-2.5 border-b"
          style={{ borderColor: 'var(--border)', background: color + '10' }}>
          <div>
            <p className="text-xs font-semibold" style={{ color }}>{POPOVER_LABELS[type]}</p>
            <p className="text-xs" style={{ color: 'var(--text-muted)' }}>{agence_nom || 'Toutes agences'}</p>
          </div>
          <button type="button" onClick={onClose} className="p-1 rounded-lg hover:bg-black/5 transition-colors">
            <X size={13} style={{ color: 'var(--text-muted)' }} />
          </button>
        </div>
        <div className="max-h-64 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center gap-2 py-6 text-xs" style={{ color: 'var(--text-muted)' }}>
              <div className="animate-spin w-4 h-4 border-2 rounded-full"
                style={{ borderColor: color, borderTopColor: 'transparent' }} />
              Chargement…
            </div>
          ) : items.length === 0 ? (
            <div className="py-6 text-center text-xs" style={{ color: 'var(--text-muted)' }}>Aucun document trouvé</div>
          ) : (
            <div className="divide-y">
              {items.map((item, i) => (
                <button key={i} type="button" onClick={() => onNavigate(type, item)}
                  className="w-full text-left px-3 py-2.5 hover:bg-black/[0.03] transition-colors flex items-center justify-between gap-2"
                  data-testid={`reassort-popover-doc-${type}-${i}`}>
                  <div className="min-w-0">
                    <p className="text-xs font-semibold truncate" style={{ color: 'var(--text)' }}>
                      {item.numero}
                    </p>
                    <p className="text-xs truncate" style={{ color: 'var(--text-muted)' }}>
                      {type === 'achats' ? item.fournisseur : item.client}
                      {item.date && <span className="ml-1" style={{ color: 'var(--text-placeholder)' }}>· {item.date}</span>}
                      {item.stade && <span className="ml-1" style={{ color: 'var(--text-placeholder)' }}>· {item.stade}</span>}
                    </p>
                  </div>
                  <span className="flex-shrink-0 text-xs font-bold tabular-nums px-2 py-0.5 rounded-full"
                    style={{ background: color + '18', color }}>{item.quantite}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </>,
    document.body
  );
}

// ── Cards config ──────────────────────────────────────────────────────────────
const FILTER_CARDS = [
  { key: 'tous',      label: 'Tous',          color: '#475569', bg: '#F8FAFC', border: '#94A3B8' },
  { key: 'ventes_sv', label: 'Stock ventes',  color: '#1D4ED8', bg: '#EFF6FF', border: '#BFDBFE' },
  { key: 'or_bloque', label: 'OR bloqués',    color: '#B91C1C', bg: '#FFF1F2', border: '#FECACA' },
  { key: 'seuil_min', label: 'Seuil min',     color: '#B45309', bg: '#FFFBEB', border: '#FDE68A' },
];

// ── Cellule seuil éditable (Mini / Maxi) ──────────────────────────────────────
function EditableSeuilCell({ value, field, articleId, stockId, editing, onStartEdit, onChangeEdit, onSaveEdit, onCancelEdit, saving }) {
  const inputRef = useRef(null);
  const isEditing = editing?.articleId === articleId && editing?.stockId === stockId && editing?.field === field;
  const isDisabled = !stockId;

  if (isDisabled) return (
    <span className="text-sm tabular-nums block text-center" style={{ color: 'var(--text-muted)' }}>—</span>
  );

  if (isEditing) {
    return (
      <input
        ref={inputRef}
        type="number"
        min="0"
        step="1"
        value={editing.value}
        onChange={e => onChangeEdit(e.target.value)}
        onBlur={() => onSaveEdit()}
        onKeyDown={e => {
          if (e.key === 'Enter') { e.preventDefault(); onSaveEdit(); }
          if (e.key === 'Escape') { e.preventDefault(); onCancelEdit(); }
        }}
        autoFocus
        data-testid={`seuil-input-${field}-${articleId}`}
        className="w-full text-center text-sm tabular-nums rounded-md border px-1 py-0.5 outline-none"
        style={{
          borderColor: field === 'min' ? '#B45309' : '#0369A1',
          boxShadow: `0 0 0 2px ${field === 'min' ? '#FDE68A' : '#BAE6FD'}`,
          background: 'white',
          color: 'var(--text)',
        }}
      />
    );
  }

  const isSaving = saving === `${articleId}__${stockId}__${field}`;
  const displayVal = value != null ? value : '—';
  const color = field === 'min' ? '#B45309' : '#0369A1';

  return (
    <button
      onClick={e => { e.stopPropagation(); onStartEdit(articleId, stockId, field, value); }}
      disabled={isSaving}
      data-testid={`seuil-btn-${field}-${articleId}`}
      className="group flex items-center justify-center gap-1 w-full px-1 py-0.5 rounded-md transition-all hover:opacity-80"
      style={{ background: 'transparent', border: '1px solid transparent' }}
      title={`Modifier le seuil ${field === 'min' ? 'minimum' : 'maximum'}`}
    >
      {isSaving
        ? <span className="text-sm tabular-nums" style={{ color: 'var(--text-muted)' }}>…</span>
        : <>
            <span className="text-sm tabular-nums" style={{ color: value != null ? color : 'var(--text-muted)' }}>
              {displayVal}
            </span>
            <Pencil size={9} className="opacity-0 group-hover:opacity-60 flex-shrink-0 transition-opacity" style={{ color }} />
          </>
      }
    </button>
  );
}

// ── Portail d'édition du fournisseur préféré ──────────────────────────────────
function FournisseurEditPortal({ editFourn, fournisseurs, onSelect, onClose }) {
  const [search, setSearch] = useState(editFourn?.currentNom || '');
  const inputRef = useRef(null);

  useEffect(() => { inputRef.current?.focus(); }, []);

  if (!editFourn) return null;
  const { rect } = editFourn;
  const top  = Math.min(rect.bottom + 6, window.innerHeight - 260);
  const left = Math.max(8, Math.min(rect.left, window.innerWidth - 280));

  const filtered = fournisseurs.filter(f =>
    !search.trim() || (f.nom || '').toLowerCase().includes(search.toLowerCase())
  );

  return createPortal(
    <>
      <div className="fixed inset-0 z-[998]" onClick={onClose} />
      <div className="fixed z-[999] rounded-xl shadow-2xl border overflow-hidden"
        style={{ top, left, width: 272, background: '#ffffff', borderColor: '#E2E8F0' }}
        onClick={e => e.stopPropagation()}
        data-testid="fourn-edit-portal">
        <div className="px-3 py-2 border-b" style={{ borderColor: 'var(--border)' }}>
          <div className="relative">
            <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none" style={{ color: 'var(--text-placeholder)' }} />
            <input
              ref={inputRef}
              value={search}
              onChange={e => setSearch(e.target.value)}
              onKeyDown={e => { if (e.key === 'Escape') { e.preventDefault(); onClose(); } }}
              placeholder="Rechercher fournisseur…"
              data-testid="fourn-edit-input"
              className="w-full pl-7 pr-3 py-1.5 text-sm border rounded-lg focus:outline-none"
              style={{ borderColor: 'var(--border)', color: 'var(--text)' }}
            />
          </div>
        </div>
        <div className="max-h-52 overflow-y-auto">
          {filtered.length === 0 ? (
            <p className="px-3 py-3 text-xs" style={{ color: 'var(--text-placeholder)' }}>Aucun résultat</p>
          ) : filtered.map(f => (
            <button key={f.entreprise_id} type="button"
              onMouseDown={() => onSelect(f.nom)}
              className="w-full text-left px-3 py-2 text-sm hover:bg-black/[0.03] transition-colors"
              style={{ borderBottom: '1px solid var(--border-light)', color: 'var(--text)' }}>
              {f.nom}
            </button>
          ))}
        </div>
      </div>
    </>,
    document.body
  );
}

// ─────────────────────────────────────────────────────────────────────────────
export default function Reassort() {
  const { activeAgences, isReadOnly } = useSociete();
  const navigate = useNavigate();
  const [besoins, setBesoins] = useState([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [filtre, setFiltre] = useState('tous');
  const [groupByFournisseur, setGroupByFournisseur] = useState(false);

  // ── État modale fusion ────────────────────────────────────────────────────────
  const [mergeModal, setMergeModal] = useState(null); // { groupes: [{fourn_key, candidates, lignes}], agence_id, selBesoins }
  const [mergeChoix, setMergeChoix] = useState({});   // fourn_key -> achat_id | "new"

  // ── État édition seuils ───────────────────────────────────────────────────────
  const [editSeuil, setEditSeuil] = useState(null); // { articleId, stockId, field, value }
  const [savingSeuil, setSavingSeuil] = useState(null); // `${articleId}__${stockId}__${field}`

  const handleStartEdit = useCallback((articleId, stockId, field, currentVal) => {
    setEditSeuil({ articleId, stockId, field, value: currentVal != null ? String(currentVal) : '' });
  }, []);

  const handleCancelEdit = useCallback(() => setEditSeuil(null), []);

  const handleSaveEdit = useCallback(async () => {
    if (!editSeuil) return;
    const { articleId, stockId, field, value } = editSeuil;
    const numVal = value === '' ? null : parseFloat(value);
    if (value !== '' && isNaN(numVal)) { setEditSeuil(null); return; }

    const savKey = `${articleId}__${stockId}__${field}`;
    setSavingSeuil(savKey);
    setEditSeuil(null);

    // Trouver les valeurs actuelles pour envoyer les deux champs
    const current = besoins.find(b => b.article_id === articleId && b.stock_id === stockId);
    const payload = {
      article_id: articleId,
      stock_id: stockId,
      quantite_min: field === 'min' ? numVal : (current?.quantite_min ?? null),
      quantite_max: field === 'max' ? numVal : (current?.quantite_max ?? null),
    };

    try {
      await updateStockSeuils(payload);
      // Mise à jour locale de l'état
      setBesoins(prev => prev.map(b => {
        if (b.article_id !== articleId || b.stock_id !== stockId) return b;
        return {
          ...b,
          quantite_min: payload.quantite_min,
          quantite_max: payload.quantite_max,
        };
      }));
      toast.success('Seuil mis à jour');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur lors de la mise à jour');
    } finally {
      setSavingSeuil(null);
    }
  }, [editSeuil, besoins]);

  // ── Fournisseur préféré — édition inline ─────────────────────────────────────
  const [fournisseurs, setFournisseurs] = useState([]);
  const [editFourn, setEditFourn] = useState(null); // { articleId, currentNom, rect }
  const [savingFourn, setSavingFourn] = useState(null); // articleId

  useEffect(() => {
    getEntreprises({ type_entreprise: 'fournisseur', slim: true, limit: 2000 })
      .then(r => setFournisseurs(Array.isArray(r.data) ? r.data : (r.data?.items || [])))
      .catch(() => {});
  }, []);

  const openEditFourn = useCallback((articleId, currentNom, e) => {
    e.stopPropagation();
    setEditFourn({ articleId, currentNom: currentNom || '', rect: e.currentTarget.getBoundingClientRect() });
  }, []);

  const closeEditFourn = useCallback(() => setEditFourn(null), []);

  const handleSelectFourn = useCallback(async (nomFourn) => {
    if (!editFourn) return;
    const { articleId } = editFourn;
    setEditFourn(null);
    setSavingFourn(articleId);
    const current = besoins.find(b => b.article_id === articleId);
    const existing = current?.fournisseur_prefere || {};
    try {
      await patchArticleFournisseurPrefere(articleId, { ...(existing || {}), nom: nomFourn });
      setBesoins(prev => prev.map(b =>
        b.article_id === articleId
          ? { ...b, fournisseur_prefere: { ...(b.fournisseur_prefere || {}), nom: nomFourn } }
          : b
      ));
      toast.success('Fournisseur préféré mis à jour');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur lors de la mise à jour');
    } finally {
      setSavingFourn(null);
    }
  }, [editFourn, besoins]);

  // ── Popover état ─────────────────────────────────────────────────────────────
  const [popover, setPopover] = useState({
    open: false, type: null, article_id: null, agence_id: null,
    agence_nom: '', items: [], loading: false, rect: null
  });

  const openPopover = useCallback(async (type, article_id, agence_id, agence_nom, e) => {
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    setPopover({ open: true, type, article_id, agence_id, agence_nom, items: [], loading: true, rect });
    try {
      const fn = type === 'ventes' ? getArticlePrevisionVentes
               : type === 'or'     ? getArticlePrevisionOR
               :                     getArticlePrevisionAchats;
      const { data } = await fn(article_id, agence_id || '_');
      setPopover(p => ({ ...p, loading: false, items: Array.isArray(data) ? data : [] }));
    } catch {
      setPopover(p => ({ ...p, loading: false, items: [] }));
    }
  }, []);

  const closePopover = useCallback(() => setPopover(p => ({ ...p, open: false })), []);

  const handlePopoverNavigate = useCallback((type, item) => {
    closePopover();
    if (type === 'ventes')  navigate(`/ventes?open_devis=${item.vente_id}`);
    else if (type === 'or') navigate(`/atelier?open_or=${item.ordre_id}`);
    else                    navigate(`/achats?open_achat=${item.achat_id}`);
  }, [closePopover, navigate]);

  // ── Chargement ───────────────────────────────────────────────────────────────
  const load = useCallback(async () => {
    setLoading(true);
    setSelected(new Set());
    try {
      const res = await getBesoinAppro((activeAgences || []).map(a => a.agence_id));
      setBesoins(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur chargement des besoins');
    } finally {
      setLoading(false);
    }
  }, [activeAgences]);

  useEffect(() => { load(); }, [load]);

  // ── Filtrage ────────────────────────────────────────────────────────────────
  const filtered = useMemo(() => {
    if (filtre === 'tous') return besoins;
    return besoins.filter(b => b.sources.some(s => s.source === filtre));
  }, [besoins, filtre]);

  const counts = useMemo(() => ({
    tous:      besoins.length,
    or_bloque: besoins.filter(b => b.sources.some(s => s.source === 'or_bloque')).length,
    seuil_min: besoins.filter(b => b.sources.some(s => s.source === 'seuil_min')).length,
    ventes_sv: besoins.filter(b => b.sources.some(s => s.source === 'ventes_sv')).length,
  }), [besoins]);

  // ── Regroupement par fournisseur ─────────────────────────────────────────────
  const groupedData = useMemo(() => {
    if (!groupByFournisseur) return null;
    const groups = {};
    filtered.forEach((b, i) => {
      const k = b.fournisseur_prefere?.nom || '__sans__';
      if (!groups[k]) groups[k] = [];
      groups[k].push({ b, i });
    });
    return Object.entries(groups).sort(([a], [b]) => {
      if (a === '__sans__') return 1;
      if (b === '__sans__') return -1;
      return a.localeCompare(b, 'fr');
    });
  }, [filtered, groupByFournisseur]);

  // ── Sélection ───────────────────────────────────────────────────────────────
  const bKey = (b, i) => `${b.article_id}__${b.stock_id || i}`;

  const toggleAll = () => {
    selected.size === filtered.length
      ? setSelected(new Set())
      : setSelected(new Set(filtered.map((b, i) => bKey(b, i))));
  };
  const toggleOne = key => setSelected(prev => {
    const next = new Set(prev);
    next.has(key) ? next.delete(key) : next.add(key);
    return next;
  });

  // ── Créer bons d'achat ───────────────────────────────────────────────────────
  const handleCreate = async () => {
    const sel = filtered
      .filter((b, i) => selected.has(bKey(b, i)))
      .map(b => ({
        article_id: b.article_id,
        reference: b.reference,
        designation: b.designation,
        quantite_manquante: Math.max(b.quantite_manquante, 1),
        fournisseur_prefere: b.fournisseur_prefere || null,
      }));
    if (!sel.length) { toast.warning('Sélectionnez au moins un article'); return; }

    const agenceId = (activeAgences || [])[0]?.agence_id || null;

    // ── Étape 1 : chercher des candidats de fusion ────────────────────────────
    setCreating(true);
    try {
      const lignesForCandidats = sel.map(b => ({
        article_id: b.article_id,
        reference: b.reference,
        designation: b.designation,
        quantite: b.quantite_manquante,
      }));
      const { data: candidates } = await getCandidatsAggregation({
        agence_id: agenceId,
        lignes: lignesForCandidats,
      });

      // S'il existe au moins un fournisseur avec une CA ouverte → modale fusion
      const groupesAvecCandidats = Object.entries(candidates || {});
      if (groupesAvecCandidats.length > 0) {
        // Pré-remplir les choix avec "new" par défaut
        const defaultChoix = {};
        groupesAvecCandidats.forEach(([k]) => { defaultChoix[k] = 'new'; });
        setMergeChoix(defaultChoix);
        setMergeModal({ groupes: groupesAvecCandidats, agenceId, selBesoins: sel });
        setCreating(false);
        return;
      }
    } catch {
      // En cas d'erreur sur candidats, on continue sans fusion
    }

    // ── Étape 2 : créer directement (sans fusion) ─────────────────────────────
    await _doCreate(sel, agenceId, {});
  };

  const _doCreate = async (sel, agenceId, choix) => {
    setCreating(true);
    try {
      const res = await creerCommandesDepuisBesoins({ besoins: sel, agence_id: agenceId, choix });
      const { created, achats } = res.data;
      toast.success(`${created} bon${created > 1 ? 's' : ''} d'achat créé${created > 1 ? 's' : ''}`);
      achats.forEach(a => {
        const label = a.action === 'ajout' ? `Lignes ajoutées à ${a.numero}` : `${a.numero} créé`;
        toast.info(`${label} — ${a.fournisseur || 'Sans fournisseur'}`, { duration: 5000 });
      });
      navigate('/achats');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur création');
    } finally { setCreating(false); }
  };

  const handleConfirmMerge = async () => {
    const { selBesoins, agenceId } = mergeModal;
    setMergeModal(null);
    await _doCreate(selBesoins, agenceId, mergeChoix);
  };

  const groupesPreview = useMemo(() => {
    const sel = filtered.filter((b, i) => selected.has(bKey(b, i)));
    const g = {};
    sel.forEach(b => { const k = b.fournisseur_prefere?.nom || 'Sans fournisseur'; g[k] = (g[k] || 0) + 1; });
    return Object.entries(g);
  }, [filtered, selected]);

  const allSelected = filtered.length > 0 && selected.size === filtered.length;
  const someSelected = selected.size > 0 && selected.size < filtered.length;
  const isMultiAgence = isReadOnly;

  // ── Sélection par groupe fournisseur ─────────────────────────────────────────
  const toggleGroup = useCallback((items) => {
    const keys = items.map(({ b, i }) => bKey(b, i));
    const allSel = keys.every(k => selected.has(k));
    setSelected(prev => {
      const next = new Set(prev);
      if (allSel) keys.forEach(k => next.delete(k));
      else keys.forEach(k => next.add(k));
      return next;
    });
  }, [selected]);

  // ── Colonnes header ──────────────────────────────────────────────────────────
  const COLS = '40px minmax(160px,1fr) 99px 83px 88px 77px 88px 88px 72px 72px 99px 165px';

  // ── Rendu d'une ligne ─────────────────────────────────────────────────────────
  const rowCells = (b, i, isSel, svColor) => (<>
    <div onClick={e => e.stopPropagation()}>
      <input type="checkbox" checked={isSel} onChange={() => toggleOne(bKey(b, i))} className="w-4 h-4 rounded" />
    </div>
    <div className="min-w-0 pr-2 flex items-center gap-1.5 overflow-hidden">
      <button
        onClick={e => { e.stopPropagation(); navigate(`/articles?open=${b.article_id}`); }}
        className="text-sm font-medium truncate flex-shrink-0 hover:underline"
        style={{ color: 'var(--primary-solid)', cursor: 'pointer', background: 'none', border: 'none', padding: 0 }}
        data-testid={`article-ref-link-${b.article_id}`}
        title={`Ouvrir la fiche ${b.reference}`}
      >
        {b.reference}
      </button>
      {b.designation && (
        <span className="text-sm truncate" style={{ color: 'var(--text-secondary)' }}>{b.designation}</span>
      )}
    </div>
    <div className="flex flex-col gap-0.5 items-center">
      {b.sources.map((s, si) => <SourceBadge key={si} source={s.source} />)}
    </div>
    <div className="text-center">
      <span className="text-sm tabular-nums" style={{ color: b.quantite_disponible <= 0 ? '#DC2626' : 'var(--text)' }}>
        {b.quantite_disponible}
      </span>
    </div>
    <div className="text-center" onClick={e => e.stopPropagation()}>
      <ClickableQty value={b.reservee_ventes} color={POPOVER_COLORS.ventes} testId={`btn-ventes-${i}`}
        onClick={e => openPopover('ventes', b.article_id, b.agence_id, b.agence_nom, e)} />
    </div>
    <div className="text-center" onClick={e => e.stopPropagation()}>
      <ClickableQty value={b.reservee_or} color={POPOVER_COLORS.or} testId={`btn-or-${i}`}
        onClick={e => openPopover('or', b.article_id, b.agence_id, b.agence_nom, e)} />
    </div>
    <div className="text-center" onClick={e => e.stopPropagation()}>
      <ClickableQty value={b.cde_fournisseur} color={POPOVER_COLORS.achats} testId={`btn-achats-${i}`}
        onClick={e => openPopover('achats', b.article_id, b.agence_id, b.agence_nom, e)} />
    </div>
    <div className="text-center">
      <span className="text-sm font-semibold tabular-nums" style={{ color: svColor }}>{b.stock_virtuel}</span>
    </div>
    <div className="flex items-center justify-center" onClick={e => e.stopPropagation()}>
      <EditableSeuilCell value={b.quantite_min} field="min" articleId={b.article_id} stockId={b.stock_id}
        editing={editSeuil} onStartEdit={handleStartEdit}
        onChangeEdit={v => setEditSeuil(prev => ({ ...prev, value: v }))}
        onSaveEdit={handleSaveEdit} onCancelEdit={handleCancelEdit} saving={savingSeuil} />
    </div>
    <div className="flex items-center justify-center" onClick={e => e.stopPropagation()}>
      <EditableSeuilCell value={b.quantite_max} field="max" articleId={b.article_id} stockId={b.stock_id}
        editing={editSeuil} onStartEdit={handleStartEdit}
        onChangeEdit={v => setEditSeuil(prev => ({ ...prev, value: v }))}
        onSaveEdit={handleSaveEdit} onCancelEdit={handleCancelEdit} saving={savingSeuil} />
    </div>
    <div className="text-center">
      <span className="text-sm font-bold tabular-nums" style={{ color: '#B91C1C' }}>
        {b.quantite_manquante > 0 ? `+${b.quantite_manquante}` : '—'}
      </span>
    </div>
    <div onClick={e => e.stopPropagation()}>
      {savingFourn === b.article_id ? (
        <span className="text-xs tabular-nums" style={{ color: 'var(--text-muted)' }}>…</span>
      ) : (
        <button
          onClick={e => openEditFourn(b.article_id, b.fournisseur_prefere?.nom || '', e)}
          className="group flex items-start gap-1 w-full text-left rounded-md px-1 py-0.5 transition-all hover:opacity-80"
          style={{ background: 'transparent', border: '1px solid transparent' }}
          data-testid={`fourn-edit-btn-${b.article_id}`}
          title="Modifier le fournisseur préféré">
          <div className="flex-1 min-w-0">
            {b.fournisseur_prefere?.nom ? (
              <>
                <p className="text-xs font-medium truncate" style={{ color: 'var(--text)' }}>{b.fournisseur_prefere.nom}</p>
                {b.fournisseur_prefere.ref_fournisseur && (
                  <p className="text-xs font-mono" style={{ color: 'var(--text-muted)' }}>{b.fournisseur_prefere.ref_fournisseur}</p>
                )}
              </>
            ) : (
              <span className="text-xs" style={{ color: 'var(--text-placeholder)' }}>—</span>
            )}
          </div>
          <Pencil size={9} className="opacity-0 group-hover:opacity-60 flex-shrink-0 mt-0.5 transition-opacity" style={{ color: 'var(--primary-solid)' }} />
        </button>
      )}
    </div>
  </>);

  return (
    <div className="space-y-5" data-testid="reassort-page">
      {/* En-tête */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold" style={{ color: 'var(--primary-solid)',  }}>
            Approvisionnement
          </h1>
          <p className="text-sm mt-0.5" style={{ color: 'var(--text-muted)' }}>
            Articles sous seuil minimum ou requis par des OR confirmés / en réparation
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setGroupByFournisseur(v => !v)}
            data-testid="group-by-fournisseur-btn"
            className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all"
            style={{
              background: groupByFournisseur ? 'var(--primary-solid)' : 'var(--bg-subtle)',
              border: `1px solid ${groupByFournisseur ? 'var(--primary-solid)' : 'var(--border)'}`,
              color: groupByFournisseur ? 'white' : 'var(--text-secondary)',
            }}>
            <Building2 size={14} />
            Par fournisseur
          </button>
          <button onClick={load} disabled={loading} data-testid="refresh-btn"
            className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all"
            style={{ background: 'var(--bg-subtle)', border: '1px solid var(--border)', color: 'var(--text-secondary)' }}>
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
            Actualiser
          </button>
        </div>
      </div>

      {/* Cards filtres */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {FILTER_CARDS.map(card => {
          const active = filtre === card.key;
          return (
            <button key={card.key} onClick={() => setFiltre(card.key)}
              data-testid={`filter-card-${card.key}`}
              className="rounded-xl p-4 text-left border transition-all hover:shadow-md"
              style={{ background: active ? card.bg : 'var(--surface)', borderColor: active ? card.border : 'var(--border)' }}>
              <div className="text-2xl font-bold" style={{ color: card.color,  }}>
                {counts[card.key]}
              </div>
              <div className="text-xs font-medium mt-0.5" style={{ color: 'var(--text-secondary)' }}>
                {card.label}
              </div>
            </button>
          );
        })}
      </div>

      {/* Tableau */}
      <div className="rounded-2xl overflow-hidden" style={{ border: '1px solid var(--border)', background: 'white' }}>
        {/* Header colonnes */}
        <div className="px-3 py-2.5 flex items-center gap-2"
          style={{ borderBottom: '1px solid var(--border)', background: 'var(--bg-subtle)' }}>
          <input type="checkbox"
            ref={el => { if (el) el.indeterminate = someSelected; }}
            checked={allSelected} onChange={toggleAll}
            className="w-4 h-4 rounded flex-shrink-0" data-testid="select-all-checkbox" />
          <span className="text-xs font-semibold flex-1" style={{ color: 'var(--text-muted)' }}>
            {filtered.length} article{filtered.length !== 1 ? 's' : ''} — {selected.size} sélectionné{selected.size !== 1 ? 's' : ''}
          </span>
        </div>

        {/* Lignes headers */}
        <div className="grid px-3 py-2 text-xs font-semibold"
          style={{ gridTemplateColumns: COLS, color: 'var(--text-muted)', borderBottom: '1px solid var(--border-light)', background: 'var(--bg-subtle)' }}>
          <div />
          <div>Article</div>
          <div className="text-center">Source</div>
          <div className="text-center">Physique</div>
          <div className="text-center">Rés. Ventes</div>
          <div className="text-center">Rés. OR</div>
          <div className="text-center">Cde Fourn.</div>
          <div className="text-center">Stock prévu</div>
          <div className="text-center">Mini</div>
          <div className="text-center">Maxi</div>
          <div className="text-center">Proposition</div>
          <div>Fournisseur préféré</div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="animate-spin w-6 h-6 border-2 border-t-transparent rounded-full"
              style={{ borderColor: 'var(--primary-solid)', borderTopColor: 'transparent' }} />
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 gap-3">
            <p className="text-sm font-medium" style={{ color: 'var(--text)' }}>Aucun besoin d'approvisionnement</p>
            <p className="text-xs" style={{ color: 'var(--text-muted)' }}>Tous les stocks sont suffisants</p>
          </div>
        ) : (
          <div>
            {groupByFournisseur && groupedData ? (
              groupedData.map(([fournisseurKey, items]) => {
                const fournisseurNom = fournisseurKey === '__sans__' ? 'Sans fournisseur préféré' : fournisseurKey;
                const groupKeys = items.map(({ b, i }) => bKey(b, i));
                const allGroupSel = groupKeys.length > 0 && groupKeys.every(k => selected.has(k));
                const someGroupSel = groupKeys.some(k => selected.has(k)) && !allGroupSel;
                return (
                  <div key={fournisseurKey}>
                    {/* En-tête de groupe */}
                    <div className="flex items-center gap-3 px-3 py-2"
                      style={{ background: 'var(--bg-subtle)', borderBottom: '1px solid var(--border-light)', borderTop: '1px solid var(--border-light)' }}>
                      <input
                        type="checkbox"
                        ref={el => { if (el) el.indeterminate = someGroupSel; }}
                        checked={allGroupSel}
                        onChange={() => toggleGroup(items)}
                        onClick={e => e.stopPropagation()}
                        data-testid={`group-checkbox-${fournisseurKey}`}
                        className="w-4 h-4 rounded flex-shrink-0 cursor-pointer"
                      />
                      <Building2 size={13} style={{ color: 'var(--primary-solid)', flexShrink: 0 }} />
                      <span className="text-xs font-semibold" style={{ color: fournisseurKey === '__sans__' ? 'var(--text-muted)' : 'var(--text)' }}>
                        {fournisseurNom}
                      </span>
                      <span className="text-xs px-1.5 py-0.5 rounded-full tabular-nums"
                        style={{ background: 'var(--primary-solid)' + '18', color: 'var(--primary-solid)', fontWeight: 600 }}>
                        {items.length}
                      </span>
                    </div>
                    {/* Lignes du groupe */}
                    {items.map(({ b, i }, gi) => {
                      const key = bKey(b, i);
                      const isSel = selected.has(key);
                      const svColor = b.stock_virtuel < 0 ? '#DC2626' : b.stock_virtuel === 0 ? '#92400E' : '#15803D';
                      return (
                        <div key={key}
                          onClick={() => toggleOne(key)}
                          className="grid px-3 py-2.5 cursor-pointer transition-colors items-center"
                          style={{
                            gridTemplateColumns: COLS,
                            borderBottom: gi < items.length - 1 ? '1px solid var(--border-light)' : 'none',
                            background: isSel ? 'var(--primary-50)' : 'white',
                          }}
                          data-testid={`besoin-row-${i}`}>
                          {rowCells(b, i, isSel, svColor)}
                        </div>
                      );
                    })}
                  </div>
                );
              })
            ) : (
              filtered.map((b, i) => {
                const key = bKey(b, i);
                const isSel = selected.has(key);
                const svColor = b.stock_virtuel < 0 ? '#DC2626' : b.stock_virtuel === 0 ? '#92400E' : '#15803D';
                return (
                  <div key={key}
                    onClick={() => toggleOne(key)}
                    className="grid px-3 py-2.5 cursor-pointer transition-colors items-center"
                    style={{
                      gridTemplateColumns: COLS,
                      borderBottom: i < filtered.length - 1 ? '1px solid var(--border-light)' : 'none',
                      background: isSel ? 'var(--primary-50)' : 'white',
                    }}
                    data-testid={`besoin-row-${i}`}>
                    {rowCells(b, i, isSel, svColor)}
                  </div>
                );
              })
            )}
          </div>
        )}

      </div>

      {/* Footer sélection */}
      {selected.size > 0 && (
        <div className="sticky bottom-4 z-20">
          <div className="rounded-2xl p-4 shadow-xl flex items-center gap-4 flex-wrap"
            style={{ background: 'var(--text)', border: '1px solid rgba(255,255,255,0.1)' }}>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-white">
                {selected.size} article{selected.size > 1 ? 's' : ''} sélectionné{selected.size > 1 ? 's' : ''}
              </p>
              {isMultiAgence ? (
                <p className="text-xs mt-0.5" style={{ color: '#FCA5A5' }}>
                  Contexte multi-agences — sélectionnez une seule agence pour créer des bons d'achat
                </p>
              ) : groupesPreview.length > 0 && (
                <p className="text-xs mt-0.5" style={{ color: 'rgba(255,255,255,0.6)' }}>
                  → {groupesPreview.length} bon{groupesPreview.length > 1 ? 's' : ''} d'achat :
                  {' '}{groupesPreview.map(([k, n]) => `${k} (${n})`).join(' · ')}
                </p>
              )}
            </div>
            <button onClick={() => setSelected(new Set())}
              className="p-2 rounded-lg transition-colors" style={{ background: 'rgba(255,255,255,0.1)', color: 'white' }}>
              <X size={14} />
            </button>
            {!isMultiAgence && (
              <button
                onClick={handleCreate}
                disabled={creating}
                data-testid="creer-commandes-btn"
                className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold transition-all"
                style={{ background: 'white', color: 'var(--text)', cursor: 'pointer' }}>
                {creating ? <RefreshCw size={14} className="animate-spin" /> : <ShoppingBag size={14} />}
                Créer les bons d'achat
                <ArrowRight size={14} />
              </button>
            )}
          </div>
        </div>
      )}

      {/* Portal édition fournisseur */}
      <FournisseurEditPortal
        editFourn={editFourn}
        fournisseurs={fournisseurs}
        onSelect={handleSelectFourn}
        onClose={closeEditFourn}
      />

      {/* Popover détail */}
      <PrevisionsPopover popover={popover} onClose={closePopover} onNavigate={handlePopoverNavigate} />

      {/* Modale fusion BC ─────────────────────────────────────────────────────── */}
      {mergeModal && createPortal(
        <div
          data-radix-portal=""
          style={{ position: 'fixed', inset: 0, zIndex: 9999, background: 'rgba(0,0,0,0.45)',
                   display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'auto' }}
          onPointerDown={e => e.stopPropagation()}
          onClick={e => { e.stopPropagation(); if (e.target === e.currentTarget) setMergeModal(null); }}
        >
          <div style={{ background: '#ffffff', borderRadius: 14, padding: 24, width: 520, maxWidth: '95vw',
                        pointerEvents: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.18)' }}
            onPointerDown={e => e.stopPropagation()}
            onClick={e => e.stopPropagation()}
          >
            <h3 style={{ fontSize: 15, fontWeight: 700, color: 'var(--text)', marginBottom: 6 }}>
              Commande(s) ouverte(s) existante(s)
            </h3>
            <p style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 18, lineHeight: 1.5 }}>
              Des bons d'achat ouverts existent déjà pour certains fournisseurs.<br />
              Choisissez d'y ajouter les nouvelles lignes ou de créer un nouveau BC.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14, marginBottom: 22 }}>
              {mergeModal.groupes.map(([fourn_key, candidates]) => (
                <div key={fourn_key} style={{ border: '1px solid var(--border)', borderRadius: 10, padding: 14 }}>
                  <p style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', marginBottom: 10 }}>
                    {fourn_key}
                  </p>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {/* Option : créer nouveau */}
                    <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13 }}>
                      <input type="radio" name={`merge_${fourn_key}`}
                        checked={mergeChoix[fourn_key] === 'new'}
                        onChange={() => setMergeChoix(prev => ({ ...prev, [fourn_key]: 'new' }))} />
                      <span style={{ color: 'var(--text)' }}>Créer un nouveau BC</span>
                    </label>
                    {/* Options : fusionner dans existant */}
                    {candidates.map(c => (
                      <label key={c.achat_id} style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13 }}>
                        <input type="radio" name={`merge_${fourn_key}`}
                          checked={mergeChoix[fourn_key] === c.achat_id}
                          onChange={() => setMergeChoix(prev => ({ ...prev, [fourn_key]: c.achat_id }))} />
                        <span style={{ color: 'var(--text)' }}>
                          Ajouter à <strong>{c.numero}</strong>
                          <span style={{ color: 'var(--text-muted)', marginLeft: 6 }}>({c.nb_lignes} ligne{c.nb_lignes !== 1 ? 's' : ''} · {c.stade})</span>
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <button onClick={() => setMergeModal(null)}
                style={{ padding: '8px 18px', borderRadius: 8, border: '1px solid var(--border)',
                         background: 'transparent', fontSize: 13, cursor: 'pointer', color: 'var(--text)' }}>
                Annuler
              </button>
              <button onClick={handleConfirmMerge} disabled={creating}
                data-testid="merge-confirm-btn"
                style={{ padding: '8px 18px', borderRadius: 8, border: 'none',
                         background: 'var(--primary-solid)', color: 'white',
                         fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                {creating ? 'Création…' : 'Confirmer'}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
